import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker as LeafletMarker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { APIProvider, Map as GoogleMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { GoogleCircle as GCircle } from '../components/maps/GoogleMapsWrappers';
import { Clock, Navigation, Route, AlertTriangle, CheckCircle, Loader2, MapPin, Gauge, Zap, Satellite, Mountain, Compass, Send, Map as MapIconLucide } from 'lucide-react';
import { formatSpeed, formatDistance, setLocaleSettings } from '../utils/timezone';
import { getIoValueColorClass as getValueColorClass } from '../utils/ioValueColors';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const LOGO_URL = '/ENSUE_LIVETRAK.webp';

// Fix Leaflet tiles not loading on initial render
const MapInvalidator = () => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 300);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
};

const STATUS_COLORS = {
  moving: '#2563eb',
  online: '#16a34a',
  idle: '#ca8a04',
  stopped: '#dc2626',
  offline: '#6b7280',
};

const createArrowIcon = (status, heading = 0) => {
  const color = '#16a34a'; // Always green
  return L.divIcon({
    className: '',
    html: `<div style="width:44px;height:44px;transform:rotate(${heading}deg);display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5"><path d="M12 2l-5 18.5L12 17l5 3.5z"/></svg>
    </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
};

const geofenceIcon = L.divIcon({
  className: '',
  html: `<div style="width:12px;height:12px;background:#ef4444;border:2px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Google Maps directional arrow marker (matching main app style)
const GoogleArrowMarker = ({ heading = 0 }) => {
  const color = '#16a34a';
  return (
    <div style={{ width: 0, height: 0, overflow: 'visible' }}>
      <div style={{
        width: '44px',
        height: '44px',
        transform: `translate(-50%, -50%) rotate(${heading}deg)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
      }}>
        <svg width="44" height="44" viewBox="0 0 24 24" fill={color} stroke="white" strokeWidth="1.5">
          <path d="M12 2l-5 18.5L12 17l5 3.5z"/>
        </svg>
      </div>
    </div>
  );
};

// Google Maps geofence target marker — uses 0-height anchor wrapper to prevent drift on zoom
const GoogleGeofenceMarker = () => (
  <div style={{ width: 0, height: 0, overflow: 'visible' }}>
    <div style={{
      width: '12px', height: '12px', background: '#ef4444',
      border: '2px solid #fff', borderRadius: '50%',
      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      transform: 'translate(-50%, -50%)',
    }} />
  </div>
);

// Google Maps wrapper with loading overlay
const GoogleMapWithLoader = ({ lat, lng, heading, darkMode, geofence_target }) => {
  const [mapReady, setMapReady] = useState(false);
  const minTimeRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => { minTimeRef.current = true; }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleTilesLoaded = useCallback(() => {
    if (minTimeRef.current) {
      setMapReady(true);
    } else {
      const wait = setTimeout(() => setMapReady(true), 1500);
      return () => clearTimeout(wait);
    }
  }, []);

  return (
    <>
      {/* Loading overlay — on top of map, fades out when ready */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${mapReady ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ zIndex: 2, backgroundColor: darkMode ? '#0f172a' : '#f1f5f9' }}
      >
        <div className="flex flex-col items-center gap-2">
          <Loader2 className={`w-6 h-6 animate-spin ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
          <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading Google Maps...</span>
        </div>
      </div>
      <div key={`gmap-${darkMode ? 'dark' : 'light'}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
        <APIProvider apiKey={GOOGLE_API_KEY}>
          <GoogleMap
            defaultCenter={{ lat, lng }}
            defaultZoom={15}
            mapId={GOOGLE_MAP_ID_NO_POI}
            colorScheme={darkMode ? 'DARK' : 'LIGHT'}
            style={{ width: '100%', height: '100%' }}
            disableDefaultUI={true}
            zoomControl={true}
            mapTypeControl={false}
            scaleControl={false}
            streetViewControl={false}
            rotateControl={false}
            fullscreenControl={false}
            mapTypeId="roadmap"
            onTilesLoaded={handleTilesLoaded}
          >
            <AdvancedMarker position={{ lat, lng }}>
              <GoogleArrowMarker heading={heading} />
            </AdvancedMarker>
            {geofence_target?.latitude && geofence_target?.longitude && (
              <>
                <AdvancedMarker position={{ lat: geofence_target.latitude, lng: geofence_target.longitude }}>
                  <GoogleGeofenceMarker />
                </AdvancedMarker>
                <GCircle
                  center={{ lat: geofence_target.latitude, lng: geofence_target.longitude }}
                  radius={geofence_target.radius || 100}
                  options={{
                    strokeColor: '#ef4444',
                    strokeWeight: 2,
                    strokeOpacity: 0.8,
                    fillColor: '#ef4444',
                    fillOpacity: 0.1,
                  }}
                />
              </>
            )}
          </GoogleMap>
        </APIProvider>
      </div>
    </>
  );
};

function haversineKm(lat1, lng1, lat2, lng2) {
  if (!lat1 || !lng1 || !lat2 || !lng2) return 0;
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Calculate bearing from point 1 to point 2 (in degrees, 0-360)
function calcBearing(lat1, lng1, lat2, lng2) {
  const toRad = (d) => d * Math.PI / 180;
  const toDeg = (r) => r * 180 / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Calculate smallest angle difference between two headings (0-180)
function angleDiff(a, b) {
  const diff = Math.abs(((a - b) + 540) % 360 - 180);
  return diff;
}

function calcStraightLineETA(lat1, lng1, lat2, lng2, speedKmh) {
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;
  const dist = haversineKm(lat1, lng1, lat2, lng2);
  const avgSpeed = Math.max(speedKmh || 40, 20);
  const hours = dist / avgSpeed;
  return { distance: dist, minutes: Math.round(hours * 60) };
}

export default function PublicTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingCmd, setSendingCmd] = useState(null);
  const [confirmCmd, setConfirmCmd] = useState(null);
  const [cmdResult, setCmdResult] = useState(null);
  const [routesBaseline, setRoutesBaseline] = useState(null); // Google Routes API baseline + road factor
  const [osrmRoute, setOsrmRoute] = useState(null); // OSRM route distance/duration
  const [showMap, setShowMap] = useState(true);
  const [darkMode, setDarkMode] = useState(true);

  // Hybrid ETA tracking refs
  const etaTrackingRef = useRef({ calibrationStraightLineKm: 0 });
  const fetchRoutesEtaFnRef = useRef(null);

  const executeSendCommand = async (cmd) => {
    setSendingCmd(cmd.id);
    setConfirmCmd(null);
    try {
      const res = await fetch(`${API_BASE}/api/public/track/${token}/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command_id: cmd.id }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Failed');
      setCmdResult({ success: true, message: result.message || 'Command sent' });
    } catch (e) {
      setCmdResult({ success: false, message: e.message || 'Failed to send command' });
    } finally {
      setSendingCmd(null);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/public/track/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Link not found or expired');
      }
      const d = await res.json();
      // Reflect the sharing account's distance/speed unit on this public link
      setLocaleSettings({ distanceUnit: d.distance_unit, speedUnit: d.speed_unit });
      setData(d);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!data || data.is_expired) return;
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [data, fetchData]);

  // Hybrid ETA: Google Routes API baseline + straight-line distance tracking
  // Instead of cumulative distance, we use live straight-line distance × road factor
  const routesIntervalRef = useRef(null);
  useEffect(() => {
    if (!data || data.is_expired || !data.use_directions_api || !GOOGLE_API_KEY) {
      setRoutesBaseline(null);
      etaTrackingRef.current = { calibrationStraightLineKm: 0 };
      return;
    }
    if (!data.latitude || !data.longitude) return;
    const gt = data.geofence_target;
    if (!gt?.latitude || !gt?.longitude) return;

    const fetchRoutesEta = async () => {
      try {
        // Compute straight-line distance at calibration time
        const straightLineAtCalibration = haversineKm(data.latitude, data.longitude, gt.latitude, gt.longitude);

        const res = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': GOOGLE_API_KEY,
            'X-Goog-FieldMask': 'routes.duration,routes.distanceMeters,routes.localizedValues',
          },
          body: JSON.stringify({
            origin: { location: { latLng: { latitude: data.latitude, longitude: data.longitude } } },
            destination: { location: { latLng: { latitude: gt.latitude, longitude: gt.longitude } } },
            travelMode: 'DRIVE',
            routingPreference: 'TRAFFIC_AWARE',
          }),
        });
        const result = await res.json();
        if (result.routes?.length > 0) {
          const route = result.routes[0];
          const durationSec = parseInt(route.duration?.replace('s', '') || '0');
          const distanceKm = (route.distanceMeters || 0) / 1000;
          const durationMin = Math.round(durationSec / 60);
          // Road factor: ratio of road distance to straight-line distance at calibration
          const roadFactor = straightLineAtCalibration > 0.1 ? distanceKm / straightLineAtCalibration : 1.3;
          // Rate: minutes per km of road distance
          const ratePerKm = durationMin / Math.max(distanceKm, 0.1);

          setRoutesBaseline({
            googleDistanceKm: distanceKm,
            googleDurationMin: durationMin,
            ratePerKm,
            roadFactor: Math.max(roadFactor, 1.0), // Road factor is always >= 1
            fetchTime: Date.now(),
            calibrationLat: data.latitude,
            calibrationLng: data.longitude,
            calibrationStraightLineKm: straightLineAtCalibration,
            localizedDuration: route.localizedValues?.duration?.text,
            localizedDistance: route.localizedValues?.distance?.text,
          });

          etaTrackingRef.current = {
            calibrationStraightLineKm: straightLineAtCalibration,
          };
        }
      } catch (e) {
        console.error('Routes API error:', e);
      }
    };

    fetchRoutesEtaFnRef.current = fetchRoutesEta;
    fetchRoutesEta();
    routesIntervalRef.current = setInterval(fetchRoutesEta, 20 * 60 * 1000);
    return () => { if (routesIntervalRef.current) clearInterval(routesIntervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.use_directions_api, data?.is_expired, GOOGLE_API_KEY]);

  // Recalibration trigger: if straight-line distance has INCREASED by >30% since calibration,
  // device has gone off-route — force a re-calibration
  useEffect(() => {
    if (!routesBaseline || !data?.latitude || !data?.longitude) return;
    const gt = data.geofence_target;
    if (!gt?.latitude || !gt?.longitude) return;
    const currentStraightLine = haversineKm(data.latitude, data.longitude, gt.latitude, gt.longitude);
    const calibrationSL = routesBaseline.calibrationStraightLineKm || 0;
    // If distance to destination increased by >30% from calibration, re-fetch
    if (calibrationSL > 0.5 && currentStraightLine > calibrationSL * 1.3) {
      if (routesIntervalRef.current) clearInterval(routesIntervalRef.current);
      fetchRoutesEtaFnRef.current?.();
      routesIntervalRef.current = setInterval(() => fetchRoutesEtaFnRef.current?.(), 20 * 60 * 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.latitude, data?.longitude]);

  // OSRM Route ETA: fetch road distance every 30s for continuous updates
  const osrmIntervalRef = useRef(null);
  useEffect(() => {
    if (!data || data.is_expired || !data.use_osrm_route) {
      setOsrmRoute(null);
      return;
    }
    if (!data.latitude || !data.longitude) return;
    const gt = data.geofence_target;
    if (!gt?.latitude || !gt?.longitude) return;

    const fetchOsrmRoute = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/public/track/${token}/osrm-route`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_lat: data.latitude,
            origin_lng: data.longitude,
            dest_lat: gt.latitude,
            dest_lng: gt.longitude,
          }),
        });
        const result = await res.json();
        if (result.distance_km != null && result.duration_min != null) {
          setOsrmRoute({
            distanceKm: result.distance_km,
            durationMin: result.duration_min,
            fetchTime: Date.now(),
          });
        }
      } catch (e) {
        console.error('OSRM route ETA error:', e);
      }
    };

    fetchOsrmRoute();
    osrmIntervalRef.current = setInterval(fetchOsrmRoute, 30 * 1000);
    return () => { if (osrmIntervalRef.current) clearInterval(osrmIntervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.use_osrm_route, data?.is_expired, data?.latitude, data?.longitude]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading tracking data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm w-full text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-lg font-bold text-slate-800 mb-2">Tracking Unavailable</h1>
          <p className="text-sm text-slate-500">{error}</p>
          <div className="mt-6 pt-4 border-t">
            <img src={LOGO_URL} alt="EnsueLIVETRAK" className="h-6 mx-auto opacity-50" />
          </div>
        </div>
      </div>
    );
  }

  const { latitude: lat, longitude: lng, speed, heading, status, device_name, last_seen, address,
    is_expired, expiry_reason, arrived_at, arrived_address, geofence_target,
    show_telemetry, show_commands, show_events, telemetry, commands, events,
    io_mappings, last_io_elements,
    show_map, show_eta, expires_at, date_format, time_format,
    map_provider } = data;
  const hasPosition = lat && lng;
  const useGoogleMap = map_provider === 'google' && GOOGLE_API_KEY;

  // Date formatting helper using creator's settings
  const formatDT = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(d.getDate()), month = pad(d.getMonth() + 1), year = d.getFullYear();
    let datePart;
    if (date_format === 'MM/DD/YYYY') datePart = `${month}/${day}/${year}`;
    else if (date_format === 'YYYY-MM-DD') datePart = `${year}-${month}-${day}`;
    else datePart = `${day}/${month}/${year}`;
    let hours = d.getHours(), mins = pad(d.getMinutes());
    if (time_format === '12h') {
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${datePart} ${hours}:${mins} ${ampm}`;
    }
    return `${datePart} ${pad(hours)}:${mins}`;
  };

  // Flatten nested IO elements (same logic as TelemetryPanel)
  const flatIO = {};
  if (last_io_elements) {
    for (const [k, v] of Object.entries(last_io_elements)) {
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        for (const [ck, cv] of Object.entries(v)) { flatIO[`${k}.${ck}`] = cv; }
      } else { flatIO[k] = v; }
    }
  }

  // Compute display value for an IO mapping
  const computeIOValue = (m) => {
    const raw = flatIO[m.io_id];
    if (raw == null) return '-';
    if (m.value_type === 'logic' || m.value_type === 'Logic') {
      return m.logic_mapping?.[String(raw)] || String(raw);
    }
    if ((m.value_type === 'multi_parameter' || m.value_type === 'Multi-Parameter') && m.secondary_io_id) {
      const v1 = flatIO[m.io_id], v2 = flatIO[m.secondary_io_id];
      if (v1 != null && v2 != null) {
        const sep = m.multi_param_separator != null ? m.multi_param_separator : ':';
        const key = `${v1}${sep}${v2}`;
        return m.multi_param_dictionary?.[key] || key;
      }
      return String(v1 ?? '-');
    }
    // Value type: calibration → formula → decimals → units
    let val = Number(raw);
    if (m.calibration?.length >= 2) {
      const sorted = [...m.calibration].sort((a, b) => a.x - b.x);
      if (val <= sorted[0].x) val = sorted[0].y;
      else if (val >= sorted[sorted.length - 1].x) val = sorted[sorted.length - 1].y;
      else {
        for (let i = 0; i < sorted.length - 1; i++) {
          if (val >= sorted[i].x && val <= sorted[i + 1].x) {
            const t = (val - sorted[i].x) / (sorted[i + 1].x - sorted[i].x);
            val = sorted[i].y + t * (sorted[i + 1].y - sorted[i].y);
            break;
          }
        }
      }
    }
    if (m.formula) {
      try { val = Function('X', `return ${m.formula.replace(/X/g, String(val))}`)(val); } catch {}
    }
    if (m.dictionary?.[String(Math.round(val))]) return m.dictionary[String(Math.round(val))];
    const dp = m.decimal_places ?? 1;
    return `${Number(val).toFixed(dp)}${m.units ? ` ${m.units}` : ''}`;
  };

  // Straight-line ETA (fallback when no enhanced ETA available)
  let eta = null;
  if (show_eta && geofence_target?.latitude && geofence_target?.longitude && hasPosition && !is_expired && expiry_reason !== 'arrived') {
    eta = calcStraightLineETA(lat, lng, geofence_target.latitude, geofence_target.longitude, speed);
  }

  // Enhanced ETA calculation — three tiers:
  // Tier 1 (Best): OSRM road distance × Google's traffic-aware ratePerKm
  // Tier 2: OSRM road distance + OSRM duration (no traffic)
  // Tier 3: Straight-line × roadFactor × ratePerKm (Google only, no OSRM)
  let liveEta = null;
  const hasGeofence = geofence_target?.latitude && geofence_target?.longitude;
  if (hasPosition && !is_expired && expiry_reason !== 'arrived' && hasGeofence) {
    const currentStraightLine = haversineKm(lat, lng, geofence_target.latitude, geofence_target.longitude);
    const bearingToDestination = calcBearing(lat, lng, geofence_target.latitude, geofence_target.longitude);
    const headingDeviation = heading != null ? angleDiff(heading, bearingToDestination) : 0;
    const isMovingAway = heading != null && speed > 3 && headingDeviation > 120;

    if (currentStraightLine < 0.5) {
      liveEta = { arriving: true, remainingKm: currentStraightLine, minutes: 0, source: 'arriving' };
    } else if (osrmRoute && osrmRoute.distanceKm != null && routesBaseline) {
      // Tier 1: OSRM road distance × Google traffic-aware rate
      const minutes = Math.round(osrmRoute.distanceKm * routesBaseline.ratePerKm);
      liveEta = {
        arriving: false,
        remainingKm: osrmRoute.distanceKm,
        minutes,
        paused: !speed || speed === 0,
        movingAway: isMovingAway,
        source: 'osrm+google',
      };
      liveEta.fetchTime = osrmRoute.fetchTime;
    } else if (osrmRoute && osrmRoute.distanceKm != null) {
      // Tier 2: OSRM only — road distance + OSRM estimated duration (no traffic)
      liveEta = {
        arriving: false,
        remainingKm: osrmRoute.distanceKm,
        minutes: Math.round(osrmRoute.durationMin),
        paused: !speed || speed === 0,
        movingAway: isMovingAway,
        source: 'osrm',
      };
      liveEta.fetchTime = osrmRoute.fetchTime;
    } else if (routesBaseline) {
      // Tier 3: Google only — straight-line × roadFactor × ratePerKm
      const estimatedRoadKm = currentStraightLine * (routesBaseline.roadFactor || 1.3);
      const minutes = Math.round(estimatedRoadKm * routesBaseline.ratePerKm);
      liveEta = {
        arriving: false,
        remainingKm: estimatedRoadKm,
        minutes,
        paused: !speed || speed === 0,
        movingAway: isMovingAway,
        source: 'google',
      };
      liveEta.fetchTime = routesBaseline.fetchTime;
      liveEta.localizedDistance = routesBaseline.localizedDistance;
    }
  }

  // Dark mode style helpers for consistency
  const dm = {
    card: darkMode ? 'bg-slate-700/50' : 'bg-slate-50',
    text: darkMode ? 'text-slate-200' : 'text-slate-700',
    textMuted: darkMode ? 'text-slate-400' : 'text-slate-500',
    label: darkMode ? 'text-slate-400' : 'text-slate-400',
  };

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'dark bg-slate-900' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className={`border-b shadow-sm px-4 py-2.5 flex items-center justify-between ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <img src={LOGO_URL} alt="EnsueLIVETRAK" className={`h-8 ${darkMode ? 'brightness-200' : ''}`} />
        <div className="flex items-center gap-3">
          {/* Dark Mode Toggle — matches main site */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
            <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Dark Mode</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={darkMode} onChange={() => setDarkMode(v => !v)} className="sr-only peer" />
              <div className={`w-9 h-5 rounded-full transition-colors
                after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full
                after:h-4 after:w-4 after:shadow after:transition-all peer-checked:after:translate-x-4
                ${darkMode ? 'bg-[#2563eb]' : 'bg-slate-300'}`}></div>
            </label>
          </div>
          {is_expired ? (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'}`}>
              <CheckCircle className="w-3 h-3" />
              {expiry_reason === 'arrived' ? 'Arrived' : 'Expired'}
            </span>
          ) : (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          )}
        </div>
      </div>

      {/* Map section — only when link creator allowed map */}
      {show_map !== false && (
        <>
        {/* Map Toggle — slide switch */}
        <div className={`border-b px-4 py-1.5 flex items-center gap-2.5 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={showMap} onChange={() => setShowMap(v => !v)} className="sr-only peer" />
            <div className="w-9 h-5 bg-slate-300 peer-checked:bg-blue-600 rounded-full transition-colors
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full
              after:h-4 after:w-4 after:shadow after:transition-all peer-checked:after:translate-x-4"></div>
          </label>
          <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Map</span>
        </div>

        {/* Map — only when toggle is on */}
        {showMap && (
        <div className="flex-1 relative" style={{ minHeight: '50vh', height: '50vh' }}>
          {hasPosition ? (
            useGoogleMap ? (
              /* Google Maps — Vector Map with Street view, no POI, dark mode support */
              <GoogleMapWithLoader
                lat={lat}
                lng={lng}
                heading={heading}
                darkMode={darkMode}
                geofence_target={geofence_target}
              />
            ) : (
              /* OSM / Leaflet map */
              <MapContainer key={`${lat}-${lng}`} center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }} zoomControl={false}>
                <MapInvalidator />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                <LeafletMarker position={[lat, lng]} icon={createArrowIcon(status, heading)} />
                {geofence_target?.latitude && geofence_target?.longitude && (
                  <>
                    <LeafletMarker position={[geofence_target.latitude, geofence_target.longitude]} icon={geofenceIcon} />
                    <Circle center={[geofence_target.latitude, geofence_target.longitude]} radius={geofence_target.radius || 100}
                      pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.1, weight: 2, dashArray: '6 4' }} />
                  </>
                )}
              </MapContainer>
            )
          ) : (
            <div className="h-full flex items-center justify-center bg-slate-100">
              <p className="text-sm text-slate-400">No position data available</p>
            </div>
          )}

        {/* Arrived overlay */}
        {is_expired && expiry_reason === 'arrived' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-[1000]">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-xs w-full mx-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-800">Tracking Finished</h2>
              <p className="text-sm text-slate-500 mt-1">{device_name || 'Device'} has arrived at destination</p>
              {arrived_at && <p className="text-xs text-slate-400 mt-2">Arrived: {formatDT(arrived_at)}</p>}
              {arrived_address && <p className="text-xs text-slate-400">{arrived_address}</p>}
            </div>
          </div>
        )}

        {/* Time expired overlay */}
        {is_expired && expiry_reason === 'time_expired' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-[1000]">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-xs w-full mx-4 text-center">
              <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-800">Tracking Expired</h2>
              <p className="text-sm text-slate-500 mt-1">This tracking link has expired</p>
              {expires_at && <p className="text-xs text-slate-400 mt-2">Expired: {formatDT(expires_at)}</p>}
            </div>
          </div>
        )}

        {/* Revoked/deactivated overlay */}
        {is_expired && expiry_reason !== 'arrived' && expiry_reason !== 'time_expired' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-[1000]">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-xs w-full mx-4 text-center">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-800">Tracking Revoked</h2>
              <p className="text-sm text-slate-500 mt-1">This tracking link has been revoked</p>
            </div>
          </div>
        )}
        </div>
      )}
      </>
      )}

      {/* Info Panel */}
      <div className={`border-t shadow-sm p-4 space-y-3 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className={`font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{device_name}</h2>
            {address && <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{address}</p>}
          </div>
          {speed > 0 && !is_expired && (
            <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-medium flex items-center gap-1">
              <Gauge className="w-3 h-3" /> {formatSpeed(speed)}
            </span>
          )}
        </div>

        <div className={`flex flex-wrap gap-3 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {last_seen && (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDT(last_seen)}</span>
          )}
          {hasPosition && (
            <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 text-blue-500 hover:text-blue-700 hover:underline">
              <MapPin className="w-3 h-3" /> {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
            </a>
          )}
        </div>

        {/* ETA */}
        {(liveEta || eta) && (
          <div className={`p-2.5 rounded-lg border space-y-1.5 ${darkMode ? 'bg-blue-950/30 border-blue-900' : 'bg-blue-50 border-blue-100'}`}>
            {/* Hybrid ETA — live-updating, traffic-aware */}
            {liveEta ? (
              liveEta.arriving ? (
                <div className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                  <CheckCircle className="w-4 h-4" />
                  Arriving at destination
                </div>
              ) : liveEta.movingAway ? (
                <>
                  <div className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                    <Navigation className="w-4 h-4" style={{ transform: 'rotate(180deg)' }} />
                    Moving away from destination
                    <span className="text-xs font-normal text-amber-500 ml-auto">
                      {formatDistance(liveEta.remainingKm)} away
                    </span>
                  </div>
                  <p className={`text-[10px] ${darkMode ? 'text-amber-500' : 'text-amber-400'}`}>
                    ETA will update when heading towards destination
                  </p>
                </>
              ) : (
                <>
                  <div className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                    {liveEta.source === 'osrm+google' || liveEta.source === 'osrm' ? <Route className="w-4 h-4" /> : <Navigation className="w-4 h-4" />}
                    ETA: {liveEta.minutes < 60 ? `${liveEta.minutes} min` : `${Math.floor(liveEta.minutes / 60)}h ${liveEta.minutes % 60}m`}
                    {liveEta.paused && <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>Stopped</span>}
                    <span className="text-xs font-normal text-blue-500 ml-auto">
                      {formatDistance(liveEta.remainingKm)} away
                    </span>
                  </div>
                  <p className={`text-[10px] ${darkMode ? 'text-blue-500' : 'text-blue-400'}`}>
                    {liveEta.source === 'osrm+google' ? 'Road distance + traffic-aware' : liveEta.source === 'osrm' ? 'Road distance' : 'Traffic-aware'} &middot; updated {liveEta.fetchTime ? `${Math.round((Date.now() - liveEta.fetchTime) / 60000)} min ago` : ''}
                  </p>
                </>
              )
            ) : eta && (
              /* Straight-line fallback ETA */
              <div className={`flex items-center gap-2 text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <Navigation className="w-4 h-4" />
                ETA: ~{eta.minutes < 60 ? `${eta.minutes} min` : `${Math.floor(eta.minutes / 60)}h ${eta.minutes % 60}m`}
                <span className="text-xs font-normal text-blue-500 ml-auto">{formatDistance(eta.distance)} away</span>
              </div>
            )}
            {geofence_target?.address && (
              <p className="text-xs text-blue-500">To: {geofence_target.address}</p>
            )}
          </div>
        )}

        {/* Telemetry Data */}
        {show_telemetry && telemetry && !is_expired && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Telemetry</p>
            <div className="grid grid-cols-3 gap-2 text-xs">
              {telemetry.satellites != null && (
                <div className={`p-2 rounded ${dm.card} text-center`}>
                  <Satellite className={`w-3.5 h-3.5 ${dm.label} mx-auto mb-0.5`} />
                  <p className={dm.label}>Satellites</p>
                  <p className={`font-semibold ${dm.text}`}>{telemetry.satellites}</p>
                </div>
              )}
              {telemetry.altitude != null && (
                <div className={`p-2 rounded ${dm.card} text-center`}>
                  <Mountain className={`w-3.5 h-3.5 ${dm.label} mx-auto mb-0.5`} />
                  <p className={dm.label}>Altitude</p>
                  <p className={`font-semibold ${dm.text}`}>{Math.round(telemetry.altitude)}m</p>
                </div>
              )}
              {telemetry.battery_voltage != null && (
                <div className={`p-2 rounded ${dm.card} text-center`}>
                  <p className={dm.label}>Battery</p>
                  <p className={`font-semibold ${dm.text}`}>{telemetry.battery_voltage}V</p>
                </div>
              )}
              {telemetry.external_voltage != null && (
                <div className={`p-2 rounded ${dm.card} text-center`}>
                  <p className={dm.label}>External</p>
                  <p className={`font-semibold ${dm.text}`}>{telemetry.external_voltage}V</p>
                </div>
              )}
              {heading != null && (
                <div className={`p-2 rounded ${dm.card} text-center`}>
                  <Compass className={`w-3.5 h-3.5 ${dm.label} mx-auto mb-0.5`} />
                  <p className={dm.label}>Heading</p>
                  <p className={`font-semibold ${dm.text}`}>{Math.round(heading)}&deg;</p>
                </div>
              )}
            </div>
            {/* IO Mapped Telemetry Data */}
            {io_mappings && io_mappings.length > 0 && (
              <div className="mt-2 space-y-0.5 border-t pt-2">
                {io_mappings.map((m) => {
                  const displayVal = computeIOValue(m);
                  const colorClass = getValueColorClass(displayVal, m.value_colors);
                  return (
                    <div key={m.id} className="flex justify-between items-center py-0.5">
                      <span className={`text-xs truncate max-w-[55%] ${dm.textMuted}`}>{m.display_name}</span>
                      <span className={`text-xs font-semibold ${colorClass || dm.text}`}>{displayVal}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Commands */}
        {show_commands && commands && commands.length > 0 && !is_expired && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Commands</p>
            <div className="grid grid-cols-2 gap-2">
              {commands.map((cmd) => {
                const bg = {
                  blue: 'bg-blue-600 hover:bg-blue-700', green: 'bg-green-600 hover:bg-green-700',
                  red: 'bg-red-600 hover:bg-red-700', yellow: 'bg-yellow-500 hover:bg-yellow-600',
                  orange: 'bg-orange-500 hover:bg-orange-600', purple: 'bg-purple-600 hover:bg-purple-700',
                  gray: 'bg-slate-500 hover:bg-slate-600', teal: 'bg-teal-600 hover:bg-teal-700',
                  pink: 'bg-pink-600 hover:bg-pink-700', indigo: 'bg-indigo-600 hover:bg-indigo-700',
                  cyan: 'bg-cyan-600 hover:bg-cyan-700', lime: 'bg-lime-600 hover:bg-lime-700',
                  rose: 'bg-rose-600 hover:bg-rose-700',
                };
                return (
                  <button key={cmd.id} onClick={() => setConfirmCmd(cmd)}
                    disabled={sendingCmd === cmd.id}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-white text-xs font-medium transition-colors ${bg[cmd.button_color] || bg.blue} disabled:opacity-50`}
                    title={cmd.description || cmd.command_text}
                  >
                    {sendingCmd === cmd.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    <span className="truncate">{cmd.button_label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Events */}
        {show_events && events && events.length > 0 && !is_expired && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Events</p>
            <div className="space-y-1">
              {events.slice(0, 5).map((evt, i) => (
                <div key={i} className="p-2 rounded bg-slate-50 text-xs text-slate-600">
                  {evt.name || evt.type || 'Event'}
                  {evt.timestamp && <span className="text-slate-400 ml-2">{formatDT(evt.timestamp)}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-2 border-t text-center">
          <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Powered by EnsueLIVETRAK</p>
        </div>
      </div>

      {/* Command Confirmation Modal */}
      {confirmCmd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
                { blue: 'bg-blue-600', red: 'bg-red-600', green: 'bg-green-600', yellow: 'bg-yellow-500', orange: 'bg-orange-500', purple: 'bg-purple-600', gray: 'bg-slate-500' }[confirmCmd.button_color] || 'bg-blue-600'
              }`}>
                <Send className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Send Command?</h3>
                <p className="text-xs text-slate-500">This will be sent to <span className="font-bold text-blue-600" data-testid="public-cmd-confirm-device-name">{device_name}</span></p>
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg mb-4 space-y-1">
              <p className="text-base font-semibold text-slate-800" data-testid="public-cmd-confirm-button-name">{confirmCmd.button_label}</p>
              {confirmCmd.description && (
                <p className="text-sm text-slate-500" data-testid="public-cmd-confirm-description">{confirmCmd.description}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmCmd(null)} className="flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={() => executeSendCommand(confirmCmd)} className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Command Result Modal */}
      {cmdResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xs w-full p-6 text-center">
            {cmdResult.success ? (
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            ) : (
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            )}
            <p className="text-sm font-medium text-slate-800">{cmdResult.message}</p>
            <button onClick={() => setCmdResult(null)} className="mt-4 px-6 py-2 rounded-lg bg-slate-100 text-sm font-medium text-slate-700 hover:bg-slate-200">OK</button>
          </div>
        </div>
      )}
    </div>
  );
}
